/**
 * Invoice Settings Configuration Loader
 *
 * Dynamically loads country-specific invoice settings configuration.
 * Falls back to default if country-specific config doesn't exist.
 *
 * Usage:
 *   import { getInvoiceSettingsConfig } from '@config/governance/invoiceSettings';
 *   const config = getInvoiceSettingsConfig('TW');
 *
 * With React Hook:
 *   import { useInvoiceSettings } from '@config/governance/invoiceSettings';
 *   const { hasInvoiceSettings, carrierTypeOptions, ... } = useInvoiceSettings('TW');
 *
 * To add a new country:
 *   1. Create a new file: invoiceSettings/jp.js
 *   2. Export default config object
 *   3. Add to countryConfigs below
 *
 * The frontend will automatically use the new config without
 * changing any component code.
 */

import { useMemo } from 'react';
import defaultConfig from './default';
import twConfig from './tw';
// import jpConfig from './jp';  // Add when needed

// Registry of country-specific configurations
const countryConfigs = {
  TW: twConfig,
  // JP: jpConfig,  // Add when needed
};

/**
 * Get invoice settings configuration for a country
 *
 * @param {string} countryCode - ISO country code (e.g., 'TW', 'JP', 'US')
 * @returns {Object} Invoice settings configuration
 */
export const getInvoiceSettingsConfig = (countryCode) => {
  if (!countryCode) {
    return defaultConfig;
  }

  const upperCode = countryCode.toUpperCase();
  return countryConfigs[upperCode] || defaultConfig;
};

/**
 * Check if a country has special invoice settings
 *
 * @param {string} countryCode - ISO country code
 * @returns {boolean}
 */
export const hasInvoiceSettings = (countryCode) => {
  const config = getInvoiceSettingsConfig(countryCode);
  return config.hasInvoiceSettings;
};

/**
 * Get carrier type options for a country
 *
 * @param {string} countryCode - ISO country code
 * @returns {Array} Array of carrier type options
 */
export const getCarrierTypeOptions = (countryCode) => {
  const config = getInvoiceSettingsConfig(countryCode);
  return config.carrierTypeOptions || [];
};

/**
 * Get invoice fields configuration for a country
 *
 * @param {string} countryCode - ISO country code
 * @returns {Array} Array of field configurations
 */
export const getInvoiceFields = (countryCode) => {
  const config = getInvoiceSettingsConfig(countryCode);
  return config.fields || [];
};

/**
 * Validate a field value for the given carrier type
 *
 * @param {string} countryCode - ISO country code
 * @param {string} fieldName - Field name
 * @param {string} value - Field value
 * @param {string} carrierType - Current carrier type
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateField = (countryCode, fieldName, value, carrierType) => {
  const config = getInvoiceSettingsConfig(countryCode);
  const rules = config.validations?.[fieldName];

  if (!rules) {
    return { valid: true };
  }

  // Check required
  if (rules.requiredFor?.includes(carrierType) && !value) {
    return { valid: false, error: rules.requiredMessage || `${fieldName} is required` };
  }

  if (!value) {
    return { valid: true };
  }

  // Check pattern (with carrier type specific patterns)
  const pattern = rules.patternFor?.[carrierType] || rules.pattern;
  const patternMessage = rules.patternMessageFor?.[carrierType] || rules.patternMessage;

  if (pattern && !pattern.test(value)) {
    return { valid: false, error: patternMessage || `${fieldName} format is invalid` };
  }

  // Custom validator
  if (rules.customValidator && config[rules.customValidator]) {
    return config[rules.customValidator](value);
  }

  return { valid: true };
};

/**
 * Check if a field is required for the given carrier type
 *
 * @param {string} countryCode - ISO country code
 * @param {string} fieldName - Field name
 * @param {string} carrierType - Current carrier type
 * @returns {boolean}
 */
export const isFieldRequired = (countryCode, fieldName, carrierType) => {
  const config = getInvoiceSettingsConfig(countryCode);
  const rules = config.validations?.[fieldName];
  return rules?.requiredFor?.includes(carrierType) || false;
};

/**
 * Get field visibility map based on carrier type
 *
 * @param {string} countryCode - ISO country code
 * @param {string} carrierType - Current carrier type
 * @returns {Object} Map of field names to visibility
 */
export const getFieldVisibility = (countryCode, carrierType) => {
  const fields = getInvoiceFields(countryCode);

  return fields.reduce((acc, field) => {
    // A field is visible if it's required for the current carrier type
    // or if it has no requiredFor restriction
    acc[field.name] = !field.requiredFor || field.requiredFor.includes(carrierType);
    return acc;
  }, {});
};

/**
 * React Hook for invoice settings
 *
 * Provides memoized access to invoice settings configuration
 * with helper methods for the current country and carrier type.
 *
 * @param {string} countryCode - ISO country code
 * @param {string} carrierType - Current carrier type (optional)
 * @returns {Object} Invoice settings with helper methods
 */
export const useInvoiceSettings = (countryCode, carrierType = 'none') => {
  return useMemo(() => {
    const config = getInvoiceSettingsConfig(countryCode);

    return {
      // Configuration values
      hasInvoiceSettings: config.hasInvoiceSettings,
      eInvoiceRequired: config.eInvoiceRequired,
      carrierTypeOptions: config.carrierTypeOptions,
      fields: config.fields,
      ui: config.ui,

      // Field visibility based on carrier type
      fieldVisibility: getFieldVisibility(countryCode, carrierType),

      // Helper methods
      isFieldRequired: (fieldName) => isFieldRequired(countryCode, fieldName, carrierType),
      validateField: (fieldName, value) => validateField(countryCode, fieldName, value, carrierType),
      formatCarrierDisplay: (type, data) => config.formatCarrierDisplay(type || carrierType, data),
      formatTaxIdDisplay: config.formatTaxIdDisplay,

      // E-invoice number formatter (country-specific)
      formatEInvoiceNumber: config.formatEInvoiceNumber || ((num) => num),

      // Tax ID validator (country-specific, e.g. TW 統一編號 checksum)
      validateTaxId: config.validateTaiwanTaxId || null,

      // @deprecated Use validateTaxId instead
      validateTaiwanTaxId: config.validateTaiwanTaxId,

      // Get dynamic field config
      getFieldConfig: (fieldName, locale = 'zh') => {
        const field = config.fields?.find(f => f.name === fieldName);
        if (!field) return null;

        const result = { ...field };

        // Apply dynamic values if available
        if (field.dynamicLabel?.[carrierType]) {
          result.label = field.dynamicLabel[carrierType][locale] || field.label;
        }
        if (field.dynamicPlaceholder?.[carrierType]) {
          result.placeholder = field.dynamicPlaceholder[carrierType][locale] || field.placeholder;
        }
        if (field.dynamicHint?.[carrierType]) {
          result.hint = field.dynamicHint[carrierType][locale] || field.hint;
        }

        return result;
      },
    };
  }, [countryCode, carrierType]);
};

export default {
  getInvoiceSettingsConfig,
  hasInvoiceSettings,
  getCarrierTypeOptions,
  getInvoiceFields,
  validateField,
  isFieldRequired,
  getFieldVisibility,
  useInvoiceSettings,
};
